import React, {useEffect, useState} from "react";
import {
    Box,
    Button,
    Collapse,
    Flex,
    FormControl,
    FormLabel,
    IconButton,
    Input,
    Select,
    SimpleGrid,
    Stack,
    Text,
    Tooltip
} from "@chakra-ui/react";
import {FiArrowDown, FiArrowUp, FiChevronDown, FiChevronRight, FiCopy, FiPlus, FiTrash2} from "react-icons/fi";
import {cloneEditableCue, createEditableCue, EditableCue, MAX_CUES} from "./cues";

type Props = {
    cues: EditableCue[];
    onChange: (cues: EditableCue[]) => void;
};

const CueBuilder = ({cues, onChange}: Props) => {
    const [template, setTemplate] = useState<string>("play_time");
    const [openCueIds, setOpenCueIds] = useState<Set<string>>(new Set());

    useEffect(() => {
        setOpenCueIds((prev) => {
            const next = new Set(prev);
            const activeIds = new Set(cues.map((cue) => cue.id));
            let changed = false;
            cues.forEach((cue) => {
                if (!next.has(cue.id)) {
                    next.add(cue.id);
                    changed = true;
                }
            });
            next.forEach((id) => {
                if (!activeIds.has(id)) {
                    next.delete(id);
                    changed = true;
                }
            });
            return changed ? next : prev;
        });
    }, [cues]);

    const buildCue = (overrides: Partial<EditableCue>) => {
        return {
            ...createEditableCue(),
            ...overrides
        };
    };

    const updateCue = (id: string, field: keyof EditableCue, value: string) => {
        onChange(cues.map((cue) => (cue.id === id ? {...cue, [field]: value} : cue)));
    };

    const addCue = () => {
        if (cues.length >= MAX_CUES) {
            return;
        }
        onChange([...cues, createEditableCue()]);
    };

    const addTemplate = () => {
        if (cues.length >= MAX_CUES) {
            return;
        }
        if (template === "button_chain" && cues.length + 2 > MAX_CUES) {
            return;
        }
        if (template === "play_time") {
            onChange([...cues, buildCue({action: "play", trigger: "global_time", atMs: "0"})]);
            return;
        }
        if (template === "pause_time") {
            onChange([...cues, buildCue({action: "pause", trigger: "global_time", atMs: "0"})]);
            return;
        }
        if (template === "stop_all") {
            onChange([...cues, buildCue({action: "stop", trigger: "global_time", atMs: "0", file: ""})]);
            return;
        }
        if (template === "button_play") {
            onChange([...cues, buildCue({action: "play", trigger: "button", triggerValue: "main"})]);
            return;
        }
        if (template === "button_chain") {
            const buttonCue = buildCue({action: "play", trigger: "button", triggerValue: "main"});
            const followCue = buildCue({action: "play", trigger: "time", afterMs: "2000"});
            onChange([...cues, buttonCue, followCue]);
        }
    };

    const duplicateCue = (index: number) => {
        const cue = cues[index];
        if (!cue || cues.length >= MAX_CUES) {
            return;
        }
        const cloned = cloneEditableCue(cue);
        const next = cues.slice();
        next.splice(index + 1, 0, cloned);
        onChange(next);
    };

    const removeCue = (index: number) => {
        const next = cues.slice();
        next.splice(index, 1);
        onChange(next);
    };

    const moveCue = (index: number, direction: number) => {
        const nextIndex = index + direction;
        if (nextIndex < 0 || nextIndex >= cues.length) {
            return;
        }
        const next = cues.slice();
        const [moved] = next.splice(index, 1);
        next.splice(nextIndex, 0, moved);
        onChange(next);
    };

    const toggleCue = (id: string) => {
        setOpenCueIds((prev) => {
            const next = new Set(prev);
            if (next.has(id)) {
                next.delete(id);
            } else {
                next.add(id);
            }
            return next;
        });
    };

    const templateNeedsTwo = template === "button_chain";
    const canAddTemplate = templateNeedsTwo ? cues.length + 2 <= MAX_CUES : cues.length < MAX_CUES;

    return (
        <Stack spacing={4}>
            <Flex align={"center"} justify={"space-between"} gap={3} wrap={"wrap"}>
                <Text color={"gray.600"}>{cues.length} / {MAX_CUES} cues</Text>
                <Flex gap={2} align={"center"} wrap={"wrap"}>
                    <Select size={"sm"} value={template} onChange={(event) => setTemplate(event.target.value)}>
                        <option value={"play_time"}>Play at time</option>
                        <option value={"pause_time"}>Pause at time</option>
                        <option value={"stop_all"}>Stop all at time</option>
                        <option value={"button_play"}>Button play</option>
                        <option value={"button_chain"}>Button + after_ms</option>
                    </Select>
                    <Button size={"sm"} onClick={addTemplate} isDisabled={!canAddTemplate}>
                        Add template
                    </Button>
                    <Button leftIcon={<FiPlus/>} size={"sm"} onClick={addCue} isDisabled={cues.length >= MAX_CUES}>
                        Add cue
                    </Button>
                </Flex>
            </Flex>

            {cues.length === 0 ? (
                <Text color={"gray.600"}>No cues yet. Add one to start building a file.</Text>
            ) : (
                <Box maxH={{base: "60vh", lg: "70vh"}} overflowY={"auto"} pr={2}>
                    <Stack spacing={4}>
                        {cues.map((cue, index) => (
                            <Box key={cue.id} borderWidth={"1px"} borderRadius={"md"} p={4}>
                                <Flex
                                    align={"center"}
                                    justify={"space-between"}
                                    mb={3}
                                    gap={2}
                                    wrap={"wrap"}
                                    role={"button"}
                                    tabIndex={0}
                                    cursor={"pointer"}
                                    onClick={() => toggleCue(cue.id)}
                                    onKeyDown={(event) => {
                                        if (event.key === "Enter" || event.key === " ") {
                                            event.preventDefault();
                                            toggleCue(cue.id);
                                        }
                                    }}
                                >
                                    <Flex align={"center"} gap={2}>
                                        <Box
                                            as={openCueIds.has(cue.id) ? FiChevronDown : FiChevronRight}
                                            color={"gray.500"}
                                            boxSize={4}
                                        />
                                        <Text fontWeight={"semibold"}>{cue.label.trim() ? cue.label : `Cue ${index + 1}`}</Text>
                                    </Flex>
                                    {openCueIds.has(cue.id) ? (
                                        <Flex gap={1}>
                                            <Tooltip label={"Move up"} hasArrow>
                                                <IconButton
                                                    aria-label={"Move cue up"}
                                                    icon={<FiArrowUp/>}
                                                    size={"sm"}
                                                    variant={"ghost"}
                                                    onClick={(event) => {
                                                        event.stopPropagation();
                                                        moveCue(index, -1);
                                                    }}
                                                    isDisabled={index === 0}
                                                />
                                            </Tooltip>
                                            <Tooltip label={"Move down"} hasArrow>
                                                <IconButton
                                                    aria-label={"Move cue down"}
                                                    icon={<FiArrowDown/>}
                                                    size={"sm"}
                                                    variant={"ghost"}
                                                    onClick={(event) => {
                                                        event.stopPropagation();
                                                        moveCue(index, 1);
                                                    }}
                                                    isDisabled={index === cues.length - 1}
                                                />
                                            </Tooltip>
                                            <Tooltip label={"Duplicate cue"} hasArrow>
                                                <IconButton
                                                    aria-label={"Duplicate cue"}
                                                    icon={<FiCopy/>}
                                                    size={"sm"}
                                                    variant={"ghost"}
                                                    onClick={(event) => {
                                                        event.stopPropagation();
                                                        duplicateCue(index);
                                                    }}
                                                    isDisabled={cues.length >= MAX_CUES}
                                                />
                                            </Tooltip>
                                            <Tooltip label={"Delete cue"} hasArrow>
                                                <IconButton
                                                    aria-label={"Delete cue"}
                                                    icon={<FiTrash2/>}
                                                    size={"sm"}
                                                    variant={"ghost"}
                                                    colorScheme={"red"}
                                                    onClick={(event) => {
                                                        event.stopPropagation();
                                                        removeCue(index);
                                                    }}
                                                />
                                            </Tooltip>
                                        </Flex>
                                    ) : null}
                                </Flex>
                                <Collapse in={openCueIds.has(cue.id)} animateOpacity>
                                    <SimpleGrid columns={{base: 1, md: 2}} spacing={3}>
                                        <FormControl>
                                            <FormLabel fontSize={"sm"}>Label</FormLabel>
                                            <Input
                                                size={"sm"}
                                                value={cue.label}
                                                onChange={(event) => updateCue(cue.id, "label", event.target.value)}
                                                placeholder={`Cue ${index + 1}`}
                                            />
                                        </FormControl>
                                        <FormControl>
                                            <FormLabel fontSize={"sm"}>Action</FormLabel>
                                            <Select
                                                size={"sm"}
                                                value={cue.action}
                                                onChange={(event) => updateCue(cue.id, "action", event.target.value)}
                                            >
                                                <option value={"play"}>play</option>
                                                <option value={"pause"}>pause</option>
                                                <option value={"stop"}>stop</option>
                                            </Select>
                                        </FormControl>
                                        <FormControl>
                                            <FormLabel fontSize={"sm"}>File</FormLabel>
                                            <Input
                                                size={"sm"}
                                                value={cue.file}
                                                onChange={(event) => updateCue(cue.id, "file", event.target.value)}
                                                placeholder={"bed.wav"}
                                            />
                                        </FormControl>
                                        <FormControl>
                                            <FormLabel fontSize={"sm"}>Trigger</FormLabel>
                                            <Select
                                                size={"sm"}
                                                value={cue.trigger}
                                                onChange={(event) => updateCue(cue.id, "trigger", event.target.value)}
                                            >
                                                <option value={"time"}>time</option>
                                                <option value={"global_time"}>global_time</option>
                                                <option value={"button"}>button</option>
                                            </Select>
                                        </FormControl>
                                        <FormControl>
                                            <FormLabel fontSize={"sm"}>Trigger value</FormLabel>
                                            <Input
                                                size={"sm"}
                                                value={cue.triggerValue}
                                                onChange={(event) => updateCue(cue.id, "triggerValue", event.target.value)}
                                                placeholder={"main"}
                                                isDisabled={cue.trigger !== "button"}
                                            />
                                        </FormControl>
                                    </SimpleGrid>

                                    <SimpleGrid columns={{base: 1, md: 3}} spacing={3} mt={3}>
                                        <FormControl>
                                            <FormLabel fontSize={"sm"}>at_ms</FormLabel>
                                            <Input
                                                size={"sm"}
                                                type={"number"}
                                                value={cue.atMs}
                                                onChange={(event) => updateCue(cue.id, "atMs", event.target.value)}
                                                placeholder={"0"}
                                                min={0}
                                                isDisabled={cue.trigger !== "global_time"}
                                            />
                                        </FormControl>
                                        <FormControl>
                                            <FormLabel fontSize={"sm"}>after_ms</FormLabel>
                                            <Input
                                                size={"sm"}
                                                type={"number"}
                                                value={cue.afterMs}
                                                onChange={(event) => updateCue(cue.id, "afterMs", event.target.value)}
                                                placeholder={"2000"}
                                                min={0}
                                                isDisabled={cue.trigger !== "time"}
                                            />
                                        </FormControl>
                                        <FormControl>
                                            <FormLabel fontSize={"sm"}>gain</FormLabel>
                                            <Input
                                                size={"sm"}
                                                type={"number"}
                                                value={cue.gain}
                                                onChange={(event) => updateCue(cue.id, "gain", event.target.value)}
                                                placeholder={"1.0"}
                                                step={"0.1"}
                                            />
                                        </FormControl>
                                        <FormControl>
                                            <FormLabel fontSize={"sm"}>fadeIn</FormLabel>
                                            <Input
                                                size={"sm"}
                                                type={"number"}
                                                value={cue.fadeIn}
                                                onChange={(event) => updateCue(cue.id, "fadeIn", event.target.value)}
                                                placeholder={"500"}
                                                min={0}
                                            />
                                        </FormControl>
                                        <FormControl>
                                            <FormLabel fontSize={"sm"}>fadeOut</FormLabel>
                                            <Input
                                                size={"sm"}
                                                type={"number"}
                                                value={cue.fadeOut}
                                                onChange={(event) => updateCue(cue.id, "fadeOut", event.target.value)}
                                                placeholder={"500"}
                                                min={0}
                                            />
                                        </FormControl>
                                    </SimpleGrid>
                                </Collapse>
                            </Box>
                        ))}
                    </Stack>
                </Box>
            )}
        </Stack>
    );
};

export default CueBuilder;
